import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import BadgesIcon from '@/assets/all-icons/badges-icon.jsx';
import SupportPresentIcon from '@/assets/all-icons/support-present-icon';
import stripeConfig, { resolveStripePublishableKey } from "@/config/stripe.config";
import useStateHandler from '@/hooks/useStateHandler';
import useTranslations from '@/i18n/useTranslations';
import BadgesService from '@/services/badges.service';
import ChannelsService from '@/services/channels.service';
import SupportService from '@/services/support.service';
import UserService from '@/services/user.service';
import ChannelButton from '@/shared-components/buttons/components/ChannelButton';
import FollowButton from '@/shared-components/buttons/components/FollowButton';
import { launchSupportCompletionConfetti } from '@/shared-components/effects/components/supportCompletionConfetti';
import Text from '@/shared-components/text/components/Text';

import { normaliseSupportConfig, normaliseSupportCurrency, SUPPORT_TITLE } from '../support/constants';
import SupportPublicPopup from '../support/SupportPublicPopup';
import SupportSetupPopup from '../support/SupportSetupPopup';

import { normaliseNameKey,PROFILE_BADGES, PROFILE_BADGES_BY_ID } from './profileBadgesConfig';
import ProfileBadgesModal from './ProfileBadgesModal';

import './styles/ProfileButton.css';

const PROFILE_CHAT_BUTTONS_ENABLED = true; // Re-enabled for profile chat/channel button fixes

function buildSupportPayload(config, { backgroundImageId, shouldUpdateBackground } = {}) {
  const enabled = Boolean(config?.enabled);
  const currency = normaliseSupportCurrency(config?.currency);

  const payload = {
    support_enabled: enabled,
    support_title: SUPPORT_TITLE,
    support_pitch: config?.pitch ? config.pitch.toString().trim() : '',
    support_currency: currency,
  };

  if (shouldUpdateBackground) {
    payload.support_background_image = backgroundImageId ? String(backgroundImageId) : '';
  }

  if (enabled) {
    const tiers = Array.isArray(config?.tiers) ? config.tiers.slice(0, 3) : [];
    payload.support_tiers = tiers.map((tier, index) => ({
      label: tier?.label ? tier.label.toString().trim() : '',
      amount: Number(tier?.amount ?? 0),
      order: index,
    }));

    const defaultAmounts = Array.isArray(config?.defaultAmounts)
      ? config.defaultAmounts
        .filter(slot => !slot?.isCustom)
        .map(slot => Number(slot?.amount))
        .filter(amount => Number.isFinite(amount) && amount > 0)
      : [];

    payload.support_default_amounts = defaultAmounts;

    if (config?.goal?.label && config.goal.label.toString().trim() && config.goal.amount) {
      payload.support_goal = {
        label: config.goal.label.toString().trim(),
        target_amount: Number(config.goal.amount),
      };
    } else if (config?.goal?.amount || config?.goal?.label) {
      payload.support_goal = null;
    }
  } else {
    payload.support_goal = null;
  }

  return payload;
}

export default function ProfileButton({ fetchData, setFetchData, internal }) {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslations('common.profile.buttons');
  const { state, stateHandler } = useStateHandler();
  const stripeInstanceRef = useRef(null);
  const [showSupportSetup, setShowSupportSetup] = useState(false);
  const [showSupportPopup, setShowSupportPopup] = useState(false);
  const [isSavingSupport, setIsSavingSupport] = useState(false);
  const [supportRequest, setSupportRequest] = useState({ loading: false, error: null });
  const [isBadgesModalOpen, setIsBadgesModalOpen] = useState(false);
  const [userBadges, setUserBadges] = useState([]);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [badgesError, setBadgesError] = useState(null);
  const [hasUnclaimedBadges, setHasUnclaimedBadges] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const badgesFetchRef = useRef({ profileId: null, promise: null });
  const setFetchDataRef = useRef(setFetchData);
  const selectedBadgeId = fetchData?.selected_badge_id || null;

  useEffect(() => {
    if (!showSupportPopup)
    {
      setSupportRequest({ loading: false, error: null });
    }
  }, [showSupportPopup]);

  const parseUnreadCount = useCallback((value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.max(0, value);
    }
    if (typeof value === "string") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    }
    return 0;
  }, []);

  const profileType = String(fetchData?.user_type || fetchData?.__t || '').toLowerCase();
  const isGalleryProfile = profileType === "gallery";
  const shouldLoadChatUnreadSummary = PROFILE_CHAT_BUTTONS_ENABLED
    && Boolean(state?.user_session?._id)
    && (internal || isGalleryProfile);

  const refreshChatUnreadCount = useCallback(async () => {
    if (!shouldLoadChatUnreadSummary) {
      setChatUnreadCount(0);
      return;
    }

    try {
      const [channelResult, dmResult] = await Promise.all([
        ChannelsService.getUnreadSummary(),
        UserService.getUserChatsUnreadSummary(),
      ]);

      const totalUnread = parseUnreadCount(channelResult?.data?.total_unread)
        + parseUnreadCount(dmResult?.data?.total_unread);
      setChatUnreadCount(totalUnread);
    }
    catch (_error) {
      // Fail silently to avoid interrupting the profile UI.
      setChatUnreadCount(0);
    }
  }, [parseUnreadCount, shouldLoadChatUnreadSummary]);

  useEffect(() => {
    refreshChatUnreadCount();
  }, [refreshChatUnreadCount]);

  useEffect(() => {
    if (!state?.channelUnreadUpdated) {
      return;
    }
    refreshChatUnreadCount();
  }, [state?.channelUnreadUpdated, refreshChatUnreadCount]);

  useEffect(() => {
    function handleWindowFocus() {
      refreshChatUnreadCount();
    }
    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [refreshChatUnreadCount]);

  function onFollowAction(followStatus) {
    if (typeof setFetchData !== 'function') {
      return;
    }

    setFetchData(prev => {
      if (!prev) {
        return prev;
      }

      const inspiring = prev.user_profile_info?.user_inspiring ?? 0;

      return {
        ...prev,
        im_following: followStatus,
        user_profile_info: {
          ...prev.user_profile_info,
          user_inspiring: inspiring + (followStatus ? 1 : -1)
        }
      };
    });
  }

  const isSupportEligibleProfile = profileType === "artist" || profileType === "gallery";
  // Normalise support configuration so the popups always have sensible defaults.
  const supportConfig = useMemo(
    () => normaliseSupportConfig(fetchData?.support_settings),
    [fetchData?.support_settings]
  );
  const isSupportEnabled = Boolean(supportConfig.enabled);
  const showSupportCta = isSupportEligibleProfile && isSupportEnabled;

  async function handleSupportSave(updatedConfig) {
    if (isSavingSupport || typeof setFetchData !== 'function') {
      return;
    }

    const profileId = fetchData?._id || fetchData?.user_id;
    if (!profileId) {
      return;
    }

    try {
      setIsSavingSupport(true);

      let backgroundImageId;
      let shouldUpdateBackground = false;

      if (updatedConfig?.backgroundImage?.file) {
        const { response, data } = await UserService.uploadImage(updatedConfig.backgroundImage.file);
        if (!response?.ok || !data?.image_id) {
          throw new Error(t('errors.support_upload_background'));
        }
        backgroundImageId = data.image_id;
        shouldUpdateBackground = true;
      } else if (!updatedConfig?.backgroundImage && supportConfig.backgroundImage?.id) {
        shouldUpdateBackground = true;
        backgroundImageId = '';
      }

      const payload = buildSupportPayload(updatedConfig, { backgroundImageId, shouldUpdateBackground });

      if (payload.support_enabled) {
        const hasInvalidTier = Array.isArray(payload.support_tiers)
          ? payload.support_tiers.some(tier => !tier.label)
          : false;

        if (hasInvalidTier) {
          throw new Error(t('errors.support_tier_description_required'));
        }
      }

      const { response, data } = await SupportService.upsertConfiguration(profileId, payload);

      if (!response?.ok || !data) {
        const message = data?.error_message || data?.message || t('errors.support_save_failed');
        throw new Error(message);
      }

      const nextConfig = normaliseSupportConfig(data);

      setFetchData(prev => {
        if (!prev) {
          return prev;
        }
        
        return {
          ...prev,
          support_settings: nextConfig
        };
      });
    } catch (error) {
      console.error('Failed to save support configuration', error);
      setShowSupportSetup(true);
      alert(error?.message || t('errors.support_save_failed'));
    } finally {
      setIsSavingSupport(false);
    }
  }

  const handleSupportConfirm = useCallback(async (payload = {}) => {
    const supportedId = fetchData?._id || fetchData?.user_id || null;
    if (!state?.user_session?._id) {
      setSupportRequest({ loading: false, error: t('errors.sign_in_required') });
      return;
    }
    if (!supportedId) {
      setSupportRequest({ loading: false, error: t('errors.profile_not_identified') });
      return;
    }

    try {
      setSupportRequest({ loading: true, error: null });
      const body = {
        amount: payload.amount,
        currency: payload.currency ?? "usd",
        vatAmount: payload.vatAmount ?? 0,
        vatRate: payload.vatRate ?? 0,
        isRecurring: payload.frequency === "monthly",
        anonymous: Boolean(payload.giveAnonymously),
        paymentMethodId: payload.paymentMethodId ?? payload.paymentMethod?.id ?? null,
        buyer_country: payload.buyerCountry ?? null,
        billing_address: payload.billingAddress ?? null,
        billing_info: payload.billingInfo ?? null,
        force_non_business_purchase: payload.forceNonBusinessPurchase === true,
      };

      const { response, data } = await SupportService.createPaymentIntent(supportedId, body);
      if (!response?.ok) {
        throw new Error(data?.message ?? t('errors.support_payment_failed'));
      }

      const paymentIntentId = data?.payment_intent_id;
      if (!paymentIntentId) {
        throw new Error(t('errors.payout_init_failed'));
      }

      const shouldConfirm =
        data?.client_secret &&
        (data?.requires_action ||
          ["requires_action", "requires_confirmation"].includes(data?.status));

      if (shouldConfirm) {
        let stripeInstance = stripeInstanceRef.current;
        if (!stripeInstance) {
          const publishableKey = stripeConfig.publishableKey || (await resolveStripePublishableKey());
          if (!publishableKey) {
            throw new Error(t('errors.stripe_load_failed'));
          }
          stripeInstance = await loadStripe(publishableKey);
          stripeInstanceRef.current = stripeInstance;
        }

        if (!stripeInstance) {
          throw new Error(t('errors.stripe_unavailable'));
        }

        const confirmPayload = {};
        if (body.paymentMethodId) {
          confirmPayload.payment_method = body.paymentMethodId;
        }
        const confirmResult = await stripeInstance.confirmCardPayment(
          data.client_secret,
          confirmPayload
        );

        if (confirmResult?.error) {
          throw confirmResult.error;
        }
      }

      const finalizeResult = await SupportService.finalizeContribution(supportedId, {
        paymentIntentId,
      });
      if (!finalizeResult?.response?.ok) {
        throw new Error(finalizeResult?.data?.message ?? t('errors.support_record_failed'));
      }

      setSupportRequest({ loading: false, error: null });
      setShowSupportPopup(false);
      launchSupportCompletionConfetti();
      stateHandler.set("temporalPopup", {
        text: body.isRecurring ? t('messages.support_recurring_scheduled') : t('messages.support_sent'),
      });
    }
    catch (error)
    {
      const message = error?.message ?? t('errors.support_process_failed');
      setSupportRequest({ loading: false, error: message });
    }
  }, [fetchData?._id, fetchData?.user_id, state?.user_session?._id, stateHandler, t]);

  useEffect(() => {
    if (!showSupportCta) {
      setShowSupportPopup(false); // Avoid lingering open popups if support gets disabled.
    }
  }, [showSupportCta]);

  useEffect(() => {
    setFetchDataRef.current = setFetchData;
  }, [setFetchData]);

  const profileId = fetchData?._id || fetchData?.user_id;

  const updateFeaturedBadgeSelection = useCallback((badges = []) => {
    const mutateFetchData = setFetchDataRef.current;
    if (typeof mutateFetchData !== 'function' || internal) {
      return;
    }

    const badgeMap = new Map();
    badges.forEach((badge) => {
      const key = normaliseNameKey(badge?.badge);
      if (key && !badgeMap.has(key)) {
        badgeMap.set(key, badge);
      }
    });

    const firstClaimedTemplateId = PROFILE_BADGES.find((template) => {
      const templateKey = normaliseNameKey(template.name);
      const serverBadge = templateKey ? badgeMap.get(templateKey) : null;
      return Boolean(serverBadge?.isClaimed);
    })?.id ?? null;

    mutateFetchData((prev) => {
      if (!prev) {
        return prev;
      }

      const currentSelectedId = prev.selected_badge_id ?? null;
      const currentTemplate = currentSelectedId ? PROFILE_BADGES_BY_ID[currentSelectedId] : null;
      const currentTemplateKey = currentTemplate ? normaliseNameKey(currentTemplate.name) : null;
      const currentIsValid = currentTemplateKey
        ? Boolean(badgeMap.get(currentTemplateKey)?.isClaimed)
        : false;
      const resolvedSelection = currentIsValid ? currentSelectedId : firstClaimedTemplateId ?? null;

      if (resolvedSelection === currentSelectedId) {
        return prev;
      }

      return {
        ...prev,
        selected_badge_id: resolvedSelection || null,
      };
    });
  }, [internal]);

  const fetchBadges = useCallback(async ({ silent = false, force = false } = {}) => {
    if (!internal) {
      setUserBadges([]);
      setHasUnclaimedBadges(false);
      return [];
    }

    if (!profileId) {
      setUserBadges([]);
      setHasUnclaimedBadges(false);
      return [];
    }

    if (!force && badgesFetchRef.current.profileId === profileId && badgesFetchRef.current.promise) {
      return badgesFetchRef.current.promise;
    }

    if (!silent) {
      setBadgesLoading(true);
      setBadgesError(null);
    }

    const requestPromise = (async () => {
      try {
        const { response, data } = await BadgesService.getUserBadges(profileId);
        if (response?.ok && Array.isArray(data?.data)) {
          const badgeList = data.data;
          setUserBadges(badgeList);
          if (internal) {
            setHasUnclaimedBadges(badgeList.some((badge) => badge && badge.isClaimed === false));
          } else {
            setHasUnclaimedBadges(false);
          }
          updateFeaturedBadgeSelection(badgeList);
          return badgeList;
        }

        throw new Error(data?.message || t('errors.badges_load_failed'));
      } catch (error) {
        if (!silent) {
          setUserBadges([]);
          setBadgesError(error);
        }
        if (internal) {
          setHasUnclaimedBadges(false);
        }
        throw error;
      } finally {
        if (!silent) {
          setBadgesLoading(false);
        }
        if (badgesFetchRef.current.promise === requestPromise) {
          badgesFetchRef.current = { profileId: null, promise: null };
        }
      }
    })();

    badgesFetchRef.current = { profileId, promise: requestPromise };
    return requestPromise;
  }, [profileId, internal, updateFeaturedBadgeSelection, t]);

  useEffect(() => {
    badgesFetchRef.current = { profileId: null, promise: null };
  }, [profileId]);

  useEffect(() => {
    if (!internal || !profileId) {
      return;
    }

    fetchBadges({ silent: false }).catch(() => {});
  }, [profileId, internal, fetchBadges]);

  useEffect(() => {
    if (!isBadgesModalOpen || !profileId) {
      return;
    }

    fetchBadges({ force: true }).catch(() => {});
  }, [isBadgesModalOpen, profileId, fetchBadges, internal]);

  const openBadgesModal = () => setIsBadgesModalOpen(true);
  const closeBadgesModal = () => setIsBadgesModalOpen(false);

  const handleBadgeClaim = useCallback(async (badgeName) => {
    if (!internal || !profileId || !badgeName) {
      return;
    }

    const { response, data } = await BadgesService.claimBadge(profileId, badgeName);
    if (!response?.ok) {
      throw new Error(data?.message || t('errors.badge_claim_failed'));
    }

    await fetchBadges({ force: true });
  }, [internal, profileId, fetchBadges, t]);

  const shouldRenderBadges = internal;

  const badgesButton = shouldRenderBadges ? (
    <div className="profile-button__badges-wrapper">
      <button
        type="button"
        className="profile-button__badges-button hover-icon-animation"
        onClick={openBadgesModal}
        aria-label={t('aria.view_badges')}
      >
        <BadgesIcon className="profile-button__badges-icon hover-icon-animation__icon" />
      </button>
      {internal && hasUnclaimedBadges && (
        <div className="new-notifications profile-button__badges-indicator" aria-hidden="true" />
      )}
    </div>
  ) : null;

  const handleBadgeSelect = (badgeId) => {
    if (!internal || !profileId || typeof setFetchData !== 'function') {
      return;
    }

    const normalizedBadgeId = badgeId ?? null;
    const previousBadgeId = fetchData?.selected_badge_id ?? null;
    if (previousBadgeId === normalizedBadgeId) {
      return;
    }

    setFetchData((prev) => {
      if (!prev) {
        return prev;
      }
      return { ...prev, selected_badge_id: normalizedBadgeId };
    });

    (async () => {
      try {
        const payload = { selected_badge_id: normalizedBadgeId };
        const { response, data } = await UserService.editProfile(payload);
        if (!response?.ok) {
          throw new Error(data?.message || t('errors.featured_badge_update_failed'));
        }
        setFetchData((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            selected_badge_id: data?.selected_badge_id ?? normalizedBadgeId,
          };
        });
      } catch (error) {
        console.error('Failed to persist badge selection', error);
        setFetchData((prev) => {
          if (!prev) {
            return prev;
          }
          return { ...prev, selected_badge_id: previousBadgeId };
        });
      }
    })();
  };

  const badgesModal = shouldRenderBadges ? (
    <ProfileBadgesModal
      open={isBadgesModalOpen}
      onClose={closeBadgesModal}
      username={fetchData?.user_username}
      selectedBadgeId={selectedBadgeId}
      onSelectBadge={handleBadgeSelect}
      isOwner={Boolean(internal)}
      userBadges={userBadges}
      badgesLoading={badgesLoading}
      badgesError={badgesError}
      onClaimBadge={internal ? handleBadgeClaim : undefined}
    />
  ) : null;

  if (internal) {
    // Internal view (owner's own profile or internal view of other users)
    const manageSupportButton = isSupportEligibleProfile ? (
      <Text
        className="profile-button__button profile-button__follow-button profile-button__support-cta font-size-medium"
        onClick={() => {
          if (!isSavingSupport) {
            setShowSupportSetup(true);
          }
        }}>
        <span className="profile-button__support-cta-label">{t('actions.support')}</span>
        <SupportPresentIcon className="profile-button__support-cta-icon" />
      </Text>
    ) : null;

    if (isGalleryProfile) {
      return (
        <>
          <div className="profile-button__buttons">
            {manageSupportButton}
            {PROFILE_CHAT_BUTTONS_ENABLED && (
              <ChannelButton
                className="profile-button__chat-button"
                onClick={() => navigate(`/channels`, { state: { fromPath: location.pathname + location.search } })}
                disabled={false}
                badgeCount={chatUnreadCount}
                large
              >
                {t('actions.chat')}
              </ChannelButton>
            )}
            {badgesButton}
          </div>

          {isSupportEligibleProfile && (
            <SupportSetupPopup
              open={showSupportSetup}
              onClose={() => setShowSupportSetup(false)}
              onSave={handleSupportSave}
              initialConfig={supportConfig}
              supportedUser={fetchData}
            />
          )}
          {badgesModal}
        </>
      );
    }

    return (
      <>
        <div className="profile-button__buttons">
          {manageSupportButton}
          {PROFILE_CHAT_BUTTONS_ENABLED && (
            <ChannelButton
              className="profile-button__chat-button"
              // Navigate to Channels as primary access for internal views
              onClick={() => navigate(`/channels`, { state: { fromPath: location.pathname + location.search } })}
              disabled={false}
              badgeCount={chatUnreadCount}
              large
            >
              {t('actions.chat')}
            </ChannelButton>
          )}
          {badgesButton}
        </div>

        {isSupportEligibleProfile && (
          <SupportSetupPopup
            open={showSupportSetup}
            onClose={() => setShowSupportSetup(false)}
            onSave={handleSupportSave}
            initialConfig={supportConfig}
            supportedUser={fetchData}
          />
        )}
        {badgesModal}
      </>
    );
  } else {
    // External view (others viewing the profile)
    return (
      <>
        <div className="profile-button__buttons">
          <FollowButton
            userId={fetchData._id}
            imFollowing={fetchData.im_following}
            isFollowingMe={fetchData.is_following_me}
            onFollowAction={onFollowAction}
            className="profile-button__follow-button"
          />
          {showSupportCta && (
            <Text
              className="profile-button__button profile-button__follow-button profile-button__support-cta font-size-medium"
              onClick={() => setShowSupportPopup(true)}>
              <span className="profile-button__support-cta-label">{t('actions.support')}</span>
              <SupportPresentIcon className="profile-button__support-cta-icon" />
            </Text>
          )}
          {PROFILE_CHAT_BUTTONS_ENABLED && isGalleryProfile && (
            <ChannelButton
              className="profile-button__chat-button"
              // For external gallery profiles, navigate to the DM conversation route
              onClick={() => {
                console.log("ProfileButton: Navigating to DM conversation for gallery:", fetchData.user_username);
                navigate(`/chat/dm/${fetchData.user_username}`);
              }}
              disabled={!fetchData.im_following}
              badgeCount={chatUnreadCount}
              large
            >
              {t('actions.chat')}
            </ChannelButton>
          )}
          {badgesButton}
        </div>
        <SupportPublicPopup
          open={showSupportCta && showSupportPopup}
          onClose={() => setShowSupportPopup(false)}
          config={supportConfig}
          supportedUser={fetchData}
          onConfirm={handleSupportConfirm}
          isSubmitting={supportRequest.loading}
          errorMessage={supportRequest.error}
        />
        {badgesModal}
      </>
    );
  }

}
